import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import {
    TableCell,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    ListItemText,
} from '@material-ui/core';

import { BasicThreeDotsMenu, Text } from 'components/shared';
import { ApplicationState } from 'store';
import { images } from 'assets';
import { getFinanceTotalAmount } from './logic';
import styles from '../../styles';
import './print-styles.css';
import financeStore from 'store/financeStore';
import { FinanceServicesData } from 'store/financeStore/reducer';
import { Charts } from './components/Charts';
import { FinanceItemDetails } from './components/FinanceItemDetails';
import DateFilters from '../DateFIlters';
import FinanceTotalAmount from '../TotalAmount';
import { theme } from 'config/theme';

export interface DateHistoryData {
    name: string;
    startDate: Date;
    endDate: Date;
    isCustom?: boolean;
}

interface FinanceProps {
    restaurantId?: string;
    isOld?: boolean;
}

const Finances = ({ restaurantId = '', isOld = false }: FinanceProps) => {
    const dispatch = useDispatch();
    const financeDetails = useSelector((state: ApplicationState) => state.finance.financeDetails);
    const financeItem = useSelector((state: ApplicationState) => state.finance.financeItem);
    const [dateFilter, setDateFilter] = useState(0);
    const [chartView, setChartView] = useState(false);
    const [selectedDatesHistory, setSelectedDatesHistory] = useState<DateHistoryData[]>([]);
    const [hoveredSwitchIcon, setHoveredSwitchIcon] = useState(0);

    const classes = styles();

    useEffect(() => {
        getFinance(1);
        return () => {
            dispatch(financeStore.actionCreators.selectFinanceItem(null));
        };
    }, []);

    function getFinance(
        dayAgo: number = 0,
        dateHistoryList?: DateHistoryData[],
        dateFrom?: Date,
        dateTo?: Date
    ) {
        if (!!!dateFrom && !!!dateTo) {
            dateFrom = new Date();
            dateTo = new Date();
            dateFrom.setDate(dateFrom.getDate() - dayAgo);
        }

        dateHistoryList && setSelectedDatesHistory(dateHistoryList);

        // Format dates to maintain local time and avoid timezone conversion
        const formatDateForServer = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

            // Format as ISO string but with local time (no timezone conversion)
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
        };

        dispatch(
            financeStore.actionCreators.getFinance(
                formatDateForServer(dateFrom!),
                formatDateForServer(dateTo!),
                restaurantId
            )
        );
    }

    function getTableTabs(isItem = false) {
        const tabs = [];

        if (isItem) {
            for (let i = 0; i <= 3; i++) {
                let title,
                    align = 'center';

                switch (i) {
                    case 0:
                        title = i18n.t('restaurant.product');
                        align = 'left';
                        break;
                    case 1:
                        title = i18n.t('restaurant.productPrice');
                        break;
                    case 2:
                        title = i18n.t('restaurant.numberOfOrders');
                        break;
                    case 3:
                        title = i18n.t('common.totalAmount');
                        align = 'right';
                        break;
                }
                tabs.push(
                    // @ts-ignore
                    <TableCell className={classes.eachTabFor4} key={i} align={align}>
                        <span>{title}</span>
                    </TableCell>
                );
            }
        } else {
            for (let i = 0; i <= 4; i++) {
                let title,
                    align = 'center';

                switch (i) {
                    case 0:
                        title = i18n.t('common.categories');
                        align = 'left';
                        break;
                    case 1:
                        title = i18n.t(`form.homeDelivery`);
                        break;
                    case 2:
                        title = i18n.t(`form.takeAway`);
                        break;
                    case 3:
                        title = i18n.t(`form.dineIn`);
                        break;
                    /* case 4:
                        title = i18n.t(`form.cancelled`);
                        break; */
                    case 4:
                        title = i18n.t('common.totalAmount');
                        align = 'right';
                        break;
                }
                tabs.push(
                    // @ts-ignore
                    <TableCell className={classes.eachTabFor5} key={i} align={align}>
                        <span
                            style={{
                                marginRight: i === 5 ? 12 : 0,
                                color: theme.palette.grey[500],
                            }}
                        >
                            {title}
                        </span>
                    </TableCell>
                );
            }
        }

        return tabs;
    }

    function getServiceTotalAmount(category: any, service: string) {
        const foundService = category.services.find(
            (eachService: { name: string }) => eachService.name === service
        );
        if (service === 'cancelled') {
            let cancelledAmount = 0;
            category.services.forEach((eachService: FinanceServicesData) => {
                eachService.cancelledDishes.forEach(
                    (cancelledDish) => (cancelledAmount += cancelledDish.amount)
                );
            });
            return cancelledAmount.toFixed(0);
        }
        if (foundService) {
            return foundService.totalAmount.toFixed(0);
        }
        return '0';
    }

    function getIconSource(i: number) {
        if (hoveredSwitchIcon > 0) {
            if (i === 0 && hoveredSwitchIcon === 1) {
                return images.icons.listSelected;
            }
            if (i === 1 && hoveredSwitchIcon === 2) {
                return images.icons.graphSelected;
            }
        }

        switch (chartView) {
            case true:
                switch (i) {
                    case 0:
                        return images.icons.list;
                    case 1:
                        return images.icons.graphSelected;
                }
                break;
            default: {
                switch (i) {
                    case 0:
                        return images.icons.listSelected;
                    case 1:
                        return images.icons.graph;
                }
            }
        }
    }

    return (
        <div className={classes.pBottom5} style={{ height: '99%' }}>
            {!isOld && (
                <div className={classes.topContainer} style={{ justifyContent: 'space-between' }}>
                    {
                        financeItem ? (
                            <div
                                className={`${classes.flexRow} ${classes.cursorHover}`}
                                onClick={() =>
                                    dispatch(financeStore.actionCreators.selectFinanceItem(null))
                                }
                            >
                                <img
                                    src={images.icons.backArrow}
                                    alt="back-arrow"
                                    className={`${classes.smallIcon} ${classes.mLeft10}`}
                                />
                                <span className={`${classes.financeTabText} ${classes.mLeft20}`}>
                                    {financeItem.name}
                                </span>
                            </div>
                        ) : (
                            <span></span>
                        ) /* (
                        <span className={classes.mLeft10}>Finance</span>
                    ) */
                    }

                    <FinanceTotalAmount
                        amount={getFinanceTotalAmount(financeItem ? [financeItem] : financeDetails)}
                    />
                </div>
            )}

            <div className={classes.p15}>
                {/*  {financeItem ? (
                    <span>{financeItem.name}</span>
                ) : (
                    <SelectedDateHistory dateHistory={selectedDatesHistory} />
                ) */}

                <div className={classes.flexRow}>
                    <DateFilters
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        getData={getFinance}
                        selectedDatesHistory={selectedDatesHistory}
                        setSelectedDatesHistory={setSelectedDatesHistory}
                        isChartsShowing={chartView}
                    />
                    <span style={{ marginLeft: 20 }}></span>
                    <div className={classes.switchGraphContainer}>
                        <img
                            onClick={() => setChartView(false)}
                            onMouseOver={() => setHoveredSwitchIcon(1)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(0)}
                            alt="switch-gaph"
                        />
                        <img
                            onClick={() => {
                                dispatch(financeStore.actionCreators.selectFinanceItem(null));
                                setChartView(true);
                            }}
                            onMouseOver={() => setHoveredSwitchIcon(2)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(1)}
                            alt="switch-gaph"
                        />
                    </div>
                    <BasicThreeDotsMenu
                        id="hide-in-print"
                        items={[
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={() => window.print()}
                            >
                                <img
                                    src={images.icons.print}
                                    alt="print-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.print')} />
                            </div>,
                        ]}
                    />
                </div>
            </div>

            {financeItem ? (
                <FinanceItemDetails financeItem={financeItem} />
            ) : chartView ? (
                <Charts
                    selectedDatesHistory={selectedDatesHistory}
                    dateFilter={dateFilter}
                    onDateClick={(selectedDates, newDateFilter, dateHistoryList) => {
                        const { startDate, endDate } = selectedDates;
                        /* setDateFilter(newDateFilter); */
                        setSelectedDatesHistory(dateHistoryList);
                        return dispatch(
                            financeStore.actionCreators.getFinance(startDate, endDate, restaurantId)
                        );
                    }}
                />
            ) : (
                <TableContainer className={classes.tableContainer}>
                    <Table aria-label="simple table" stickyHeader>
                        <TableHead>
                            <TableRow key="table-head">{getTableTabs(!!financeItem)}</TableRow>
                        </TableHead>

                        <TableBody>
                            {!financeItem &&
                                financeDetails?.map((financeDetail, i: number) => (
                                    <TableRow
                                        onClick={() =>
                                            dispatch(
                                                financeStore.actionCreators.selectFinanceItem({
                                                    ...financeDetail,
                                                })
                                            )
                                        }
                                        className={classes.basicHover}
                                        hover={true}
                                        key={i}
                                    >
                                        <TableCell component={'td'} align={'left'}>
                                            <Text text={financeDetail.name} maxLength={30} />
                                        </TableCell>
                                        <TableCell component={'td'} align={'center'}>
                                            {getServiceTotalAmount(financeDetail, 'HomeDelivery')}
                                        </TableCell>
                                        <TableCell component={'td'} align={'center'}>
                                            {getServiceTotalAmount(financeDetail, 'TakeAway')}
                                        </TableCell>
                                        <TableCell component={'td'} align={'center'}>
                                            {getServiceTotalAmount(financeDetail, 'DineIn')}
                                        </TableCell>
                                        {/* <TableCell component={'td'} align={'center'}>
                                            {getServiceTotalAmount(financeDetail, 'cancelled')}
                                        </TableCell> */}
                                        <TableCell component={'td'} align={'right'}>
                                            {financeDetail.amount}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    );
};

export default Finances;
